import { Injectable } from "@angular/core";
import { eroiIds, TvdMetricData } from "../types/tvd-types";
import { TVDService } from "../services/tvd.service";

/**
 * Resolves a drill-in metric from the current TVD model.
 *
 * The TVD UI does not always have a 1:1 relationship with the metrics
 * returned by the API. Some UI tiles represent a single API metric,
 * while others are composite metrics that are constructed from multiple
 * API metrics.
 *
 * Example:
 * - UI Assets tile
 * - API repair metric
 * - API replacement metric
 *
 * This resolver acts as the single place where those mapping rules live,
 * preventing components from needing to understand TVD-specific
 * composition logic.
 */
@Injectable({
  providedIn: 'root'
})
export class TvdMetricResolver {
  constructor(private _tvdService: TVDService) {}

  /**
   * Resolves a metric for the given UI metric identifier.
   *
   * For most metrics this is a simple lookup against the current TVD model.
   *
   * Composite metrics are handled specially and returned as a synthesized
   * TvdMetricData object so callers can consume them exactly the same way
   * they consume standard API metrics.
   *
   * @param metricId UI metric identifier.
   * @param groupId UI group identifier.
   * @returns Resolved metric data or undefined when the metric cannot
   *          be found in the current model.
   */
  resolve(metricId: string,  groupId: string): TvdMetricData | undefined {
    const group = this._tvdService
      .getCurrentTvdModel()
      ?.tvdResponse
      .find(g => g.id === groupId);

    if (!group) {
      return undefined;
    }

    const metrics = group.indvMetrics ?? [];

    /**
     * Assets is a composite UI metric.
     *
     * The API does not expose an "assets" metric directly.
     * Instead it returns:
     * - repair
     * - replacement
     *
     * The UI presents these as a single Assets tile, so we aggregate
     * both metrics into a synthetic TvdMetricData object.
     */
    // TODO:
    // If additional composite metrics are introduced, replace the hardcoded
    // logic below with a composition registry (metricId -> resolver strategy)
    // so all composite metric rules can be managed centrally.
    if (metricId === eroiIds.ASSETS) {
      const repair = metrics.find(m => m.id === 'repair');
      const replacement = metrics.find(m => m.id === 'replacement');

      if (!repair && !replacement) {
        return undefined;
      }

      return {
        id: eroiIds.ASSETS,

        // Sum the original API contributions to construct the
        // displayed Assets monetary contribution.
        monetaryContribution:
          (
            Number(repair?.monetaryContribution?.replace(/,/g, '') || 0) +
            Number(replacement?.monetaryContribution?.replace(/,/g, '') || 0)
          ).toLocaleString(),

        // Sum modified contributions so the existing display pipeline
        // can continue using modified/original value semantics.
        modifiedMonetaryContribution:
          (
            Number(repair?.modifiedMonetaryContribution?.replace(/,/g, '') || 0) +
            Number(replacement?.modifiedMonetaryContribution?.replace(/,/g, '') || 0)
          ).toLocaleString(),

        // Assets currently exposes only monetary values.
        valueContribution: '0',
        modifiedValueContribution: '0'
      } as TvdMetricData;
    }

    /**
     * Standard metric resolution.
     *
     * Most UI metrics map directly to a single API metric.
     */
    return metrics.find(m => m.id === metricId);
  }
}